import React from 'react';
import styled from 'styled-components';
import intl from 'react-intl-universal';
import { Menu, MenuItem } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { FormattedMessage as T } from '@/components/FormattedMessage';

import classNames from 'classnames';
import { CLASSES } from '@/constants/classes';
import { firstLettersArgs } from '@/utils';

/**
 * project suggest field.
 * @returns
 */
export function ProjectSuggestField({
  projects,
  initialProjectId,
  selectedProjectId,
  defaultSelectText = intl.get('select_project'),
  popoverFill = false,
  onProjectSelected,
  ...suggestProps
}: any) {
  const initialProject = React.useMemo(
    () => projects.find((b: any) => b.id === initialProjectId),
    [initialProjectId, projects],
  );

  const [selectedProject, setSelectedProject] = React.useState(
    initialProject || null,
  );
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (typeof selectedProjectId !== 'undefined') {
      const project = selectedProjectId
        ? projects.find((a: any) => a.id === selectedProjectId)
        : null;
      setSelectedProject(project);
    }
  }, [selectedProjectId, projects, setSelectedProject]);

  /**
   * @param {*} project
   * @param {*} param1
   * @returns {JSX.Element}
   */
  const projectsItemRenderer = (
    project: any,
    { handleClick, modifiers, query }: any,
  ) => {
    return (
      <MenuItem
        icon={<AvatarSelect text={project} />}
        disabled={modifiers.disabled}
        key={project.id}
        text={project.name}
        onClick={handleClick}
      />
    );
  };

  /**
   *
   * @param {*} query
   * @param {*} project
   * @param {*} _index
   * @param {*} exactMatch
   * @returns
   */
  const projectsItemPredicate = (
    query: any,
    project: any,
    _index: any,
    exactMatch: any,
  ) => {
    const normalizedTitle = project.name.toLowerCase();
    const normalizedQuery = query.toLowerCase();

    if (exactMatch) {
      return normalizedTitle === normalizedQuery;
    } else {
      return (
        `${project.name}. ${normalizedTitle}`.indexOf(normalizedQuery) >= 0
      );
    }
  };

  /**
   *
   * @param {*} project
   * @returns
   */
  const projectItemSelect = React.useCallback(
    (project: any) => {
      if (project.id) {
        setSelectedProject({ ...project });
        onProjectSelected && onProjectSelected(project);
        setIsOpen(false);
      }
    },
    [setSelectedProject, onProjectSelected],
  );

  // Handle input click to open dropdown
  const handleInputClick = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  /**
   *
   * @param {*} inputVaue
   * @returns
   */
  const projectInputValueRenderer = (inputValue: any) => {
    if (inputValue) {
      return inputValue.name.toString();
    }
    return '';
  };

  // Merge input props with onClick handler
  const mergedInputProps = {
    placeholder: defaultSelectText,
    onClick: handleInputClick,
    ...suggestProps.inputProps,
  };

  // Merge popover props with isOpen state
  const mergedPopoverProps = {
    minimal: true,
    boundary: 'window',
    isOpen: isOpen,
    onInteraction: (nextOpenState: any) => setIsOpen(nextOpenState),
    ...suggestProps.popoverProps,
  };

  return (
    <Suggest
      items={projects}
      noResults={<MenuItem disabled={true} text={<T id={'no_accounts'} />} />}
      itemRenderer={projectsItemRenderer}
      itemPredicate={projectsItemPredicate}
      onItemSelect={projectItemSelect}
      selectedItem={selectedProject}
      inputProps={mergedInputProps}
      resetOnClose={true}
      fill={true}
      popoverProps={mergedPopoverProps}
      inputValueRenderer={projectInputValueRenderer}
      className={classNames(CLASSES.FORM_GROUP_LIST_SELECT, {
        [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
      })}
      {...suggestProps}
    />
  );
}

const AvatarSelect = ({ text }: any) => {
  return <AvatarContent>{firstLettersArgs(text?.name)}</AvatarContent>;
};

const AvatarContent = styled.div`
  display: inline-block;
  background: #adbcc9;
  text-align: center;
  font-weight: 400;
  color: #fff;
  height: 22px;
  width: 22px;
  line-height: 22px;
  font-size: 12px;
`;
